/**
 * Dark Theme
 *
 * Consumes design tokens and produces a fully configured MUI theme with palette.mode: 'dark', component overrides.
 *
 * @see ADR-006: Central Theming
 * @see ADR-013: UI Scaling and Measurement Units Policy
 */
import { createTheme, type ThemeOptions } from '@mui/material/styles';

import { accents, border, brand, buttons, darkMode, debug, surface, typography } from './tokens';

// ---------------------------------------------------------------------------
// Module augmentation — expose brand palette to TypeScript
// ---------------------------------------------------------------------------
declare module '@mui/material/styles' {
    interface Palette {
        debug: typeof debug;
        brand: typeof brand;
        border: typeof border;
        surface: typeof surface;
        accents: typeof accents;
        buttons: typeof buttons;
        darkMode: typeof darkMode;
    }
    interface PaletteOptions {
        debug?: typeof debug;
        brand?: typeof brand;
        border?: typeof border;
        surface?: typeof surface;
        accents?: typeof accents;
        buttons?: typeof buttons;
        darkMode?: typeof darkMode;
    }
}

// ---------------------------------------------------------------------------
// Scrollbar styles (global)
// ---------------------------------------------------------------------------
const scrollbarStyles = {
    '::-webkit-scrollbar': {
        width: '7px',
    },
    '::-webkit-scrollbar-thumb': {
        backgroundColor: brand.purple[60],
        borderRadius: '4px',
    },
    '::-webkit-scrollbar-thumb:hover': {
        backgroundColor: '#00355180',
    },
    '::-webkit-scrollbar-track': {
        backgroundColor: 'rgba(85, 85, 85, 0.4)',
        borderRadius: '4px',
    },
    '::-webkit-scrollbar-track:hover': {
        backgroundColor: 'rgba(33, 33, 33, 0.7)',
        borderRadius: '4px',
    },
    '@supports (scrollbar-color: auto)': {
        'body.is-firefox .slide_in_drawer, body.is-firefox .notification-list, body.is-firefox .content-container': {
            scrollbarColor: `${brand.purple[60]} rgba(85, 85, 85, 0.4)`,
            scrollbarWidth: 'thin' as const,
            overflowY: 'scroll' as const,
        },
    },
} as const;

// ---------------------------------------------------------------------------
// Theme definition
// ---------------------------------------------------------------------------
const themeOptions: ThemeOptions = {
    palette: {
        mode: 'dark',

        primary: {
            main: brand.purple[60],
            light: brand.purple[40],
            dark: brand.purple[80],
            contrastText: darkMode.neutral[100],
        },
        secondary: {
            main: brand.orange[50],
            light: brand.orange[30],
            dark: brand.orange[70],
            contrastText: darkMode.neutral[100],
        },
        background: {
            default: '#17151f',
            paper: '#22202e',
        },
        text: {
            primary: darkMode.neutral[80],
            secondary: brand.lightBlue[50],
            disabled: '#aaaaaa',
        },

        accents,
        buttons,
        debug,
        brand,
        border,
        surface,
        darkMode,
    },

    typography: {
        fontFamily: typography.fontFamily,
        fontWeightRegular: typography.weights.regular,
        fontWeightBold: typography.weights.semiBold,
        h1: { fontWeight: typography.weights.semiBold },
        h2: { fontWeight: typography.weights.semiBold },
        h3: { fontWeight: typography.weights.semiBold },
        h4: { fontWeight: typography.weights.semiBold },
        h5: { fontWeight: typography.weights.semiBold },
        h6: { fontWeight: typography.weights.semiBold },
    },

    shape: {
        borderRadius: 0,
    },

    components: {
        MuiCssBaseline: {
            styleOverrides: (themeParam) => ({
                ...scrollbarStyles,

                html: {
                    fontSize: '16px',
                    [themeParam.breakpoints.down('xl')]: { fontSize: '14px' },
                    [themeParam.breakpoints.down('lg')]: { fontSize: '13px' },
                    [themeParam.breakpoints.down('md')]: { fontSize: '12px' },
                    [themeParam.breakpoints.down('sm')]: { fontSize: '11px' },
                },

                body: { userSelect: 'none' },
                'input, textarea, [contenteditable="true"]': { userSelect: 'text' },

                img: {
                    display: 'none',
                    '&[src]': { display: 'block' },
                },

                '.no-select': {
                    userSelect: 'none',
                },
                '*': {
                    scrollbarColor: `${darkMode.purpleSoft[10]} ${themeParam.palette.background.paper}`,
                },
            }),
        },

        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: darkMode.purple[10],
                    color: darkMode.neutral[100],
                    border: `1px solid ${darkMode.purpleSoft[10]}`,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
                    marginTop: 8,
                    minWidth: 220,
                    backgroundImage: 'none',

                    '& .MuiListItemIcon-root': {
                        color: '#e0e0e0',
                        minWidth: 32,
                    },
                },
                list: {
                    paddingTop: 0,
                    paddingBottom: 0,
                },
            },
        },

        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: darkMode.neutral[100],
                    '&:hover': {
                        backgroundColor: brand.purple[100],
                    },
                    '&.Mui-selected': {
                        backgroundColor: brand.purple[50],
                        '&:hover': {
                            backgroundColor: brand.purple[40],
                        },
                    },
                    '&.Mui-focusVisible': {
                        backgroundColor: 'rgba(255,255,255,0.08)',
                    },
                },
            },
        },

        MuiSelect: {
            defaultProps: {
                variant: 'standard' as const,
                disableUnderline: true,
            },
            styleOverrides: {
                select: {
                    color: darkMode.neutral[100],
                },
                icon: {
                    color: darkMode.neutral[100],
                },
            },
        },

        MuiTooltip: {
            defaultProps: {
                followCursor: false,
                enterDelay: 500,
                leaveDelay: 0,
                placement: 'bottom',
            },
            styleOverrides: {
                tooltip: {
                    backgroundColor: darkMode.purple[10],
                    border: 'none',
                    borderRadius: '0.3rem',
                    color: darkMode.neutral[100],
                    fontSize: typography.sizes.bodyLarge,
                    '& p': {
                        margin: 0,
                    },
                    '& .MuiDivider-root': {
                        marginTop: '0.5rem',
                        marginBottom: '0.5rem',
                        borderColor: darkMode.neutral[80],
                        width: '100%',
                    },
                },
            },
        },

        MuiDialog: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#22202e',
                    color: darkMode.neutral[80],
                    backgroundImage: 'none',
                },
            },
        },

        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: surface.overlay,
                    backgroundImage: 'none',
                },
            },
        },

        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 0,
                },
            },
        },

        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 0,
                    textTransform: 'none' as const,
                },
            },
        },

        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
            },
        },
    },
};

const darkTheme = createTheme(themeOptions);

export default darkTheme;
